import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import apiFetch, { getHeadquarters } from "../helpers/requests";

const CreateLicenseModal = ({
  setShowCreateLicenseModal,
  handleCreateLicense,
  headquarterId: initialHeadquarterId,
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [expirationDate, setExpirationDate] = useState("");
  const [headquarters, setHeadquarters] = useState<any[]>([]);
  const [selectedHQId, setSelectedHQId] = useState(initialHeadquarterId || "");
  const [isLoadingHQ, setIsLoadingHQ] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchHQs = async () => {
      setIsLoadingHQ(true);
      try {
        const data = await getHeadquarters();
        setHeadquarters(data);
        if (!selectedHQId && data.length > 0) {
            // No default selection to force user to choose or use initial
        }
      } catch (error) {
        console.error("Error fetching headquarters:", error);
      } finally {
        setIsLoadingHQ(false);
      }
    };

    fetchHQs();
  }, []);

  const createLicense = async (licenseData) => {
    try {
      const newLicense = await apiFetch(`/createLicense`, {
        method: "POST",
        body: JSON.stringify(licenseData),
      });
      return newLicense;
    } catch (error) {
      console.error("Error creating license:", error);
      throw new Error("An error occurred while creating the license");
    }
  };

  const handleCreateLicenseSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setFormSubmitted(true);
    setErrorMessage("");

    if (!selectedHQId) return;

    if (expirationDate) {
        const year = new Date(expirationDate).getFullYear();
        if (year > 9999) {
            setErrorMessage("Invalid_date_year_too_large");
            return;
        }
    }

    const licenseData = {
      headquarterId: selectedHQId,
      expirationDate: expirationDate === "" ? null : expirationDate,
    };

    try {
        const newLicense = await createLicense(licenseData);
        // Find selected HQ name for the UI update
        const selectedHQ = headquarters.find(h => h.id === selectedHQId);
        handleCreateLicense({ 
            ...newLicense, 
            headquarterName: selectedHQ ? selectedHQ.name : ""
        });
    } catch (error) {
        console.error("Failed to create license:", error);
    }
  };

  return (
    <div className="confirmation-modal">
      <div className="modal-content">
        <div
          style={{ textAlign: "center", fontSize: "1.3em", marginTop: "2vh" }}
        >
          {t("Create_a_new_license")}
        </div>
        <form onSubmit={handleCreateLicenseSubmit} className="m-4">
          
          {/* Headquarter Selection */}
          <div className="mb-3">
            <label
              htmlFor="hqSelect"
              style={{
                marginLeft: "20px",
                color: "#404040",
                fontWeight: "bold",
              }}
            >
              {t("Headquarter")}
            </label>
            <select
              id="hqSelect"
              className="form-control"
              value={selectedHQId}
              onChange={(e) => setSelectedHQId(e.target.value)}
              required
              style={{
                backgroundColor: "#CCCCCC",
                borderRadius: "20px",
                color: "#404040",
                height: "5vh",
                maxHeight: "40px",
                fontSize: "1em",
                paddingLeft: "20px",
              }}
            >
              <option value="" disabled hidden>
                {t("Select_Headquarter")}
              </option>
              {headquarters.map((hq) => (
                <option key={hq.id} value={hq.id}>
                  {hq.name}
                </option>
              ))}
            </select>
            {formSubmitted && !selectedHQId && (
              <label style={{ marginLeft: "20px", color: "#A63939", fontSize: "0.8em" }}>
                {t("Headquarter_is_required")}
              </label>
            )}
          </div>

          <div className="mb-3">
            <label
              htmlFor="expirationDateInput"
              style={{
                marginLeft: "20px",
                color: "#404040",
                fontWeight: "bold",
              }}
            >
              {t("Expiration_Date")}
            </label>
            <input
              type="date"
              className="form-control"
              id="expirationDateInput"
              value={expirationDate}
              onChange={(e) => {
                setExpirationDate(e.target.value);
                setFormSubmitted(false);
                setErrorMessage("");
              }}
              style={{
                backgroundColor: "#CCCCCC",
                borderRadius: "20px",
                color: "#404040",
                height: "5vh",
                maxHeight: "40px",
                fontSize: "1em",
                paddingLeft: "20px",
              }}
            />
          </div>

          {errorMessage && (
            <div
              style={{
                textAlign: "center",
                color: "#A63939",
                fontSize: "0.8em",
                marginBottom: "1vh",
              }}
            >
              {t(errorMessage)}
            </div>
          )}

          <div className="button-container">
            <button
              type="button"
              className="buttonCancel btn btn-primary"
              onClick={() => setShowCreateLicenseModal(false)}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary buttonAcceptGreen"
              disabled={isLoadingHQ}
            >
              {t("Create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLicenseModal;
